import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as L from 'leaflet';
import { firstValueFrom } from 'rxjs';
import { PredictionDriftHost, PredictionMode, SelectedDetection } from './prediction-drift.types';

export interface PredictionDriftPaymentDialogData {
  cost: number;
}

@Component({
  selector: 'app-prediction-drift-payment-dialog',
  templateUrl: './prediction-drift-payment-dialog.component.html',
  styleUrls: ['./prediction-drift-payment-dialog.component.scss'],
})
export class PredictionDriftPaymentDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<PredictionDriftPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PredictionDriftPaymentDialogData
  ) {}

  cancel(): void {
    this.dialogRef.close('cancelled');
  }

  payNow(): void {
    this.dialogRef.close('paid');
  }
}

// Style constants
const PREDICTION_DETECTION_ORIGINAL_STYLE: L.PathOptions = {
  color: 'magenta',
  fillColor: 'magenta',
  fillOpacity: 1.0,
  weight: 2,
};

const PREDICTION_DETECTION_HOVER_STYLE: L.PathOptions = {
  color: '#2196F3',
  fillColor: 'magenta',
  fillOpacity: 0.95,
  weight: 2,
};

const PREDICTION_DETECTION_SELECTED_STYLE: L.PathOptions = {
  color: '#1565C0',
  fillColor: 'magenta',
  fillOpacity: 0.85,
  weight: 3,
};

/**
 * Owns all Prediction Drift state and logic, extracted from
 * LeafletNewcollectionsComponent to keep the parent lean.
 *
 * Lifecycle: instantiated once per component instance via
 *   `readonly predictionDrift = new PredictionDriftController(this);`
 */
export class PredictionDriftController {
  // ── Panel visibility ─────────────────────────────────────────────────────
  active = false;
  paymentRedirecting = false;

  // ── Mode ─────────────────────────────────────────────────────────────────
  predictionMode: PredictionMode = 'select';

  // ── Selection state ───────────────────────────────────────────────────────
  selectionEnabled = false;
  selectedPredictionPolygons: SelectedDetection[] = [];
  private readonly maxSelectedDetections = 3;
  private maxAllowedNoticeElement: HTMLElement | null = null;
  private maxAllowedNoticeTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Cost ─────────────────────────────────────────────────────────────────
  predictionCost = 0;
  predictionWeatherCost = 0;
  predictionFeedCost = 0;
  predictionWeatherPoint = 0;
  predictionFeedPoint = 0;
  predictionPointTotal = 0;
  predictionCostCurrency = 'USD';
  predictionFeedName = '';
  predictionCostLoading = false;
  predictionCostError = '';
  private predictionQuoteFeeds: any = [];
  private predictionQuoteRequests: any[] = [];
  private predictionCostRequestSeq = 0;

  // ── Payment ───────────────────────────────────────────────────────────────
  paymentCompleted = false;
  predictionStarted = false;

  // ── "Run For All" count ───────────────────────────────────────────────────
  allDetectionsCount = 0;

  // ── Internal: track leaflet layer → original style ───────────────────────
  private predictionDetectionLayer: L.Layer | null = null;
  private readonly trackedLayers = new Map<L.Path, { originalStyle: L.PathOptions; selected: boolean }>();
  private readonly selectedNumberMarkers = new Map<L.Path, L.Marker>();
  private readonly onClickBound = (e: L.LeafletEvent): void => this.onLayerClick(e);
  private readonly onMouseoverBound = (e: L.LeafletEvent): void => this.onLayerMouseover(e);
  private readonly onMouseoutBound = (e: L.LeafletEvent): void => this.onLayerMouseout(e);

  constructor(private readonly host: PredictionDriftHost) {}

  // ── Panel toggle ─────────────────────────────────────────────────────────

  toggle(checked: boolean): void {
    this.active = checked;
    if (!checked) {
      this.deactivate();
      return;
    }

    this.paymentCompleted = false;
    this.predictionStarted = false;

    if (this.predictionMode === 'select') {
      this.enableSelection();
    } else {
      this.disableSelection();
      this.countAllPredictionDetections();
    }

    this.host.changDetectionRef.markForCheck();
  }

  // ── Mode change ──────────────────────────────────────────────────────────

  onModeChange(mode: PredictionMode): void {
    this.dismissMaxAllowedNotice();
    this.predictionMode = mode;
    this.paymentCompleted = false;
    this.predictionStarted = false;
    this.predictionCost = 0;
    this.predictionWeatherCost = 0;
    this.predictionFeedCost = 0;
    this.predictionWeatherPoint = 0;
    this.predictionFeedPoint = 0;
    this.predictionPointTotal = 0;
    this.predictionCostCurrency = 'USD';
    this.predictionFeedName = '';
    this.predictionCostError = '';
    this.predictionQuoteFeeds = [];
    this.predictionQuoteRequests = [];
    this.clearSelectedDetectionMapLabels();

    if (mode === 'select') {
      this.selectedPredictionPolygons = [];
      this.enableSelection();
      this.clearAllLayerHighlights();
      this.refreshPredictionCost();
    } else {
      this.disableSelection();
      this.selectedPredictionPolygons = [];
      this.countAllPredictionDetections();
      this.highlightAllPredictionDetections();
    }
    this.host.changDetectionRef.markForCheck();
  }

  // ── Selection mode ────────────────────────────────────────────────────────

  registerPredictionDetectionLayer(layer: L.Layer | null | undefined): void {
    if (!layer) return;

    if (this.predictionDetectionLayer && this.predictionDetectionLayer !== layer) {
      this.detachLayerListeners();
      this.clearSelectedDetectionMapLabels();
      this.restoreAllLayerStyles();
      this.trackedLayers.clear();
      this.selectedPredictionPolygons = [];
    }

    this.predictionDetectionLayer = layer;

    if (this.selectionEnabled) {
      this.attachLayerListeners();
    }

    this.countAllPredictionDetections();
    this.host.changDetectionRef.markForCheck();
  }

  enableSelection(): void {
    if (this.selectionEnabled) return;
    this.selectionEnabled = true;
    this.attachLayerListeners();
    this.host.changDetectionRef.markForCheck();
  }

  disableSelection(): void {
    if (!this.selectionEnabled) return;
    this.selectionEnabled = false;
    this.detachLayerListeners();
    this.host.changDetectionRef.markForCheck();
  }

  // ── Count all prediction detections (Mode 2) ──────────────────────────────

  countAllPredictionDetections(): void {
    let count = 0;
    const layer = this.predictionDetectionLayer as any;
    if (!layer || typeof layer.eachLayer !== 'function') {
      this.allDetectionsCount = count;
      return;
    }

    layer.eachLayer((sublayer: any) => {
      if (sublayer instanceof L.Polygon || sublayer instanceof L.Polyline) {
        count++;
      }
    });
    this.allDetectionsCount = count;
    if (this.active && this.predictionMode === 'all') {
      this.refreshPredictionCost();
    }
    this.host.changDetectionRef.markForCheck();
  }

  // ── Remove single detection ───────────────────────────────────────────────

  removeDetection(id: string): void {
    this.dismissMaxAllowedNotice();
    this.selectedPredictionPolygons = this.selectedPredictionPolygons.filter(d => d.id !== id);
    // Restore style on the corresponding tracked layer
    this.trackedLayers.forEach((state, layer) => {
      const feature = (layer as any).feature;
      const featureId = this.resolveFeatureId(feature);
      if (featureId === id) {
        state.selected = false;
        layer.setStyle(state.originalStyle);
      }
    });
    this.refreshSelectedDetectionMapLabels();
    this.refreshPredictionCost();
    this.host.changDetectionRef.markForCheck();
  }

  clearSelectedDetections(): void {
    this.dismissMaxAllowedNotice();
    this.selectedPredictionPolygons = [];
    this.predictionCost = 0;
    this.predictionWeatherCost = 0;
    this.predictionFeedCost = 0;
    this.predictionWeatherPoint = 0;
    this.predictionFeedPoint = 0;
    this.predictionPointTotal = 0;
    this.predictionCostCurrency = 'USD';
    this.predictionFeedName = '';
    this.predictionCostError = '';
    this.predictionCostLoading = false;
    this.predictionQuoteFeeds = [];
    this.predictionQuoteRequests = [];
    this.predictionCostRequestSeq++;
    this.trackedLayers.forEach((state, layer) => {
      state.selected = false;
      try {
        layer.setStyle(state.originalStyle);
      } catch {
        // ignore style reset failures for detached layers
      }
    });
    this.clearSelectedDetectionMapLabels();
    this.host.changDetectionRef.markForCheck();
  }

  private clearAllLayerHighlights(): void {
    this.trackedLayers.forEach((state, layer) => {
      state.selected = false;
      try {
        layer.setStyle(state.originalStyle);
      } catch {
        // ignore style reset failures for detached layers
      }
    });
    this.clearSelectedDetectionMapLabels();
  }

  private highlightAllPredictionDetections(): void {
    this.trackedLayers.forEach((state, layer) => {
      state.selected = true;
      try {
        layer.setStyle(PREDICTION_DETECTION_SELECTED_STYLE);
      } catch {
        // ignore style failures for detached layers
      }
    });
  }

  // ── Payment ───────────────────────────────────────────────────────────────

  openPaymentDialog(): void {
    void this.submitDriftCheckout();
  }

  // ── Clean up on deactivate ────────────────────────────────────────────────

  deactivate(): void {
    this.dismissMaxAllowedNotice();
    this.active = false;
    this.disableSelection();
    this.selectedPredictionPolygons = [];
    this.allDetectionsCount = 0;
    this.paymentCompleted = false;
    this.predictionStarted = false;
    this.paymentRedirecting = false;
    this.predictionMode = 'select';
    this.predictionCost = 0;
    this.predictionWeatherCost = 0;
    this.predictionFeedCost = 0;
    this.predictionWeatherPoint = 0;
    this.predictionFeedPoint = 0;
    this.predictionPointTotal = 0;
    this.predictionCostCurrency = 'USD';
    this.predictionFeedName = '';
    this.predictionCostLoading = false;
    this.predictionCostError = '';
    this.predictionQuoteFeeds = [];
    this.predictionQuoteRequests = [];
    this.predictionCostRequestSeq++;
    this.restoreAllLayerStyles();
    this.clearSelectedDetectionMapLabels();
    this.trackedLayers.clear();
    this.predictionDetectionLayer = null;
    this.host.changDetectionRef.markForCheck();
  }

  // ── Private: attach/detach listeners to prediction detection layers ───────

  private attachLayerListeners(): void {
    const layer = this.predictionDetectionLayer as any;
    if (!layer || typeof layer.eachLayer !== 'function') return;

    layer.eachLayer((sublayer: any) => {
      if (sublayer instanceof L.Polygon || sublayer instanceof L.Polyline) {
        const path = sublayer as L.Path;
        const currentStyle = (path as any).options ?? PREDICTION_DETECTION_ORIGINAL_STYLE;
        const trackedState = this.trackedLayers.get(path);
        if (!trackedState) {
          this.trackedLayers.set(path, { originalStyle: { ...currentStyle }, selected: false });
        }
        path.off('click', this.onClickBound);
        path.off('mouseover', this.onMouseoverBound);
        path.off('mouseout', this.onMouseoutBound);
        path.on('click', this.onClickBound);
        path.on('mouseover', this.onMouseoverBound);
        path.on('mouseout', this.onMouseoutBound);
        const element = (path as any).getElement?.();
        if (element?.style) element.style.cursor = 'pointer';
      }
    });
  }

  private detachLayerListeners(): void {
    this.trackedLayers.forEach((_state, layer) => {
      layer.off('click', this.onClickBound);
      layer.off('mouseover', this.onMouseoverBound);
      layer.off('mouseout', this.onMouseoutBound);
      const el = (layer as any).getElement?.();
      if (el?.style) el.style.cursor = '';
    });
  }

  private restoreAllLayerStyles(): void {
    this.trackedLayers.forEach((state, layer) => {
      try { layer.setStyle(state.originalStyle); } catch {}
    });
  }

  private refreshSelectedDetectionMapLabels(): void {
    this.trackedLayers.forEach((state, layer) => {
      const feature = (layer as any).feature;
      const number = state.selected
        ? this.getSelectedDetectionNumber(this.resolveFeatureId(feature))
        : 0;

      if (!number) {
        this.removeSelectedDetectionMarker(layer);
        return;
      }

      const latLng = this.getLayerLabelLatLng(layer);
      if (!latLng || !this.host.map) {
        return;
      }

      const marker = this.selectedNumberMarkers.get(layer);
      if (marker) {
        marker.setLatLng(latLng);
        marker.setIcon(this.createSelectedDetectionIcon(number));
        return;
      }

      this.selectedNumberMarkers.set(
        layer,
        L.marker(latLng, {
          icon: this.createSelectedDetectionIcon(number),
          interactive: false,
          keyboard: false,
          zIndexOffset: 1000,
        }).addTo(this.host.map)
      );
    });
  }

  private clearSelectedDetectionMapLabels(): void {
    this.selectedNumberMarkers.forEach((marker) => {
      try {
        marker.removeFrom(this.host.map);
      } catch {}
    });
    this.selectedNumberMarkers.clear();
  }

  private removeSelectedDetectionMarker(layer: L.Path): void {
    const marker = this.selectedNumberMarkers.get(layer);
    if (!marker) return;

    try {
      marker.removeFrom(this.host.map);
    } catch {}
    this.selectedNumberMarkers.delete(layer);
  }

  private getSelectedDetectionNumber(id: string): number {
    const index = this.selectedPredictionPolygons.findIndex((detection) => detection.id === id);
    return index >= 0 ? index + 1 : 0;
  }

  private getLayerLabelLatLng(layer: L.Path): L.LatLng | null {
    const candidate = layer as any;
    if (typeof candidate.getBounds === 'function') {
      const bounds = candidate.getBounds();
      if (bounds?.isValid?.()) {
        return bounds.getCenter();
      }
    }

    if (typeof candidate.getLatLng === 'function') {
      return candidate.getLatLng();
    }

    return null;
  }

  private createSelectedDetectionIcon(number: number): L.DivIcon {
    return L.divIcon({
      className: 'prediction-drift-number-icon',
      html: `<span style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:999px;background:#1059a9;color:#ffffff;border:2px solid #ffffff;box-shadow:0 4px 10px rgba(16,89,169,.32);font:700 12px/1 Barlow,Arial,sans-serif;">${number}</span>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
  }

  // ── Private: event handlers ───────────────────────────────────────────────

  private onLayerClick(e: L.LeafletEvent): void {
    L.DomEvent.stopPropagation(e as L.LeafletMouseEvent);
    const layer = e.target as L.Path;
    const state = this.trackedLayers.get(layer);
    if (!state) return;

    const feature = (layer as any).feature;
    const id = this.resolveFeatureId(feature);

    if (state.selected) {
      // Deselect
      this.dismissMaxAllowedNotice();
      state.selected = false;
      layer.setStyle(state.originalStyle);
      this.selectedPredictionPolygons = this.selectedPredictionPolygons.filter(d => d.id !== id);
    } else {
      if (this.selectedPredictionPolygons.length >= this.maxSelectedDetections) {
        this.showMaxAllowedNotice();
        return;
      }

      // Select
      state.selected = true;
      layer.setStyle(PREDICTION_DETECTION_SELECTED_STYLE);
      const detection: SelectedDetection = {
        id,
        geometry: feature?.geometry ?? null,
        coordinates: feature?.geometry?.coordinates ?? null,
        properties: feature?.properties ?? {},
      };
      this.selectedPredictionPolygons = [...this.selectedPredictionPolygons, detection];
    }
    this.refreshSelectedDetectionMapLabels();
    this.refreshPredictionCost();
    this.host.changDetectionRef.markForCheck();
  }

  dismissMaxAllowedNotice(): void {
    if (this.maxAllowedNoticeTimer !== null) {
      clearTimeout(this.maxAllowedNoticeTimer);
      this.maxAllowedNoticeTimer = null;
    }
    this.maxAllowedNoticeElement?.remove();
    this.maxAllowedNoticeElement = null;
  }

  private showMaxAllowedNotice(): void {
    if (!this.maxAllowedNoticeElement) {
      const notice = L.DomUtil.create(
        'div',
        'prediction-drift-max-notice',
        this.host.map.getContainer()
      );
      notice.setAttribute('role', 'alert');
      const icon = L.DomUtil.create('span', 'prediction-drift-max-notice__icon', notice);
      icon.textContent = '!';
      icon.setAttribute('aria-hidden', 'true');
      const copy = L.DomUtil.create('div', 'prediction-drift-max-notice__copy', notice);
      L.DomUtil.create('strong', '', copy).textContent = 'Maximum Allowed';
      L.DomUtil.create('span', '', copy).textContent =
        'A maximum of 3 detections can be selected.';
      const close = L.DomUtil.create(
        'button',
        'prediction-drift-max-notice__close',
        notice
      ) as HTMLButtonElement;
      close.type = 'button';
      close.textContent = '×';
      close.setAttribute('aria-label', 'Dismiss maximum allowed message');
      close.addEventListener('click', () => this.dismissMaxAllowedNotice());
      L.DomEvent.disableClickPropagation(notice);
      L.DomEvent.disableScrollPropagation(notice);
      this.maxAllowedNoticeElement = notice;
    }
    if (this.maxAllowedNoticeTimer !== null) {
      clearTimeout(this.maxAllowedNoticeTimer);
    }
    this.maxAllowedNoticeTimer = setTimeout(() => this.dismissMaxAllowedNotice(), 4000);
  }

  private onLayerMouseover(e: L.LeafletEvent): void {
    const layer = e.target as L.Path;
    const state = this.trackedLayers.get(layer);
    if (!state) return;
    if (state.selected) return;
    layer.setStyle(PREDICTION_DETECTION_HOVER_STYLE);
    const el = (layer as any).getElement?.();
    if (el?.style) el.style.cursor = 'pointer';
  }

  private onLayerMouseout(e: L.LeafletEvent): void {
    const layer = e.target as L.Path;
    const state = this.trackedLayers.get(layer);
    if (!state) return;
    if (state.selected) return;
    layer.setStyle(state.originalStyle);
  }

  // ── Private: helpers ──────────────────────────────────────────────────────

  private resolveFeatureId(feature: any): string {
    return String(
      feature?.id ??
      feature?.properties?.id ??
      feature?.properties?.detection_id ??
      JSON.stringify(feature?.geometry?.coordinates ?? '').slice(0, 40)
    );
  }

  private getCheckoutDetections(): SelectedDetection[] {
    if (this.predictionMode === 'all') {
      return this.getAllPredictionDetections();
    }

    return this.selectedPredictionPolygons;
  }

  private getAllPredictionDetections(): SelectedDetection[] {
    const detections: SelectedDetection[] = [];
    const layer = this.predictionDetectionLayer as any;

    if (!layer || typeof layer.eachLayer !== 'function') {
      return detections;
    }

    layer.eachLayer((sublayer: any) => {
      if (!(sublayer instanceof L.Polygon || sublayer instanceof L.Polyline)) {
        return;
      }

      const feature = (sublayer as any).feature;
      detections.push({
        id: this.resolveFeatureId(feature),
        geometry: feature?.geometry ?? null,
        coordinates: feature?.geometry?.coordinates ?? null,
        properties: feature?.properties ?? {},
      });
    });

    return detections;
  }

  private async refreshPredictionCost(): Promise<void> {
    const detections = this.getCheckoutDetections();
    if (!detections.length) {
      this.predictionCost = 0;
      this.predictionWeatherCost = 0;
      this.predictionFeedCost = 0;
      this.predictionWeatherPoint = 0;
      this.predictionFeedPoint = 0;
      this.predictionPointTotal = 0;
      this.predictionCostCurrency = 'USD';
      this.predictionCostLoading = false;
      this.predictionCostError = '';
      this.predictionQuoteFeeds = [];
      this.predictionQuoteRequests = [];
      this.host.changDetectionRef.markForCheck();
      return;
    }

    const requestSeq = ++this.predictionCostRequestSeq;
    this.predictionFeedName = this.getPredictionFeedName();
    this.predictionCostLoading = true;
    this.predictionCostError = '';
    this.predictionQuoteFeeds = [];
    this.predictionQuoteRequests = [];
    this.host.changDetectionRef.markForCheck();

    try {
      const payload = this.buildPredictionCostPayload(detections);
      if (!payload.predictionInfo.length || !payload.target?.length) {
        this.predictionCost = 0;
        this.predictionWeatherCost = 0;
        this.predictionFeedCost = 0;
        this.predictionWeatherPoint = 0;
        this.predictionFeedPoint = 0;
        this.predictionPointTotal = 0;
        this.predictionCostCurrency = 'USD';
        this.predictionCostError = 'Unable to retrieve the latest cost because prediction details are missing.';
        this.predictionQuoteFeeds = [];
        this.predictionQuoteRequests = [];
        return;
      }

      const response: any = await firstValueFrom(
        this.host.apiService.getWeatherCostByPredictionInfo(payload)
      );

      if (requestSeq !== this.predictionCostRequestSeq) {
        return;
      }

      const predictionQuote = this.resolvePredictionQuotePayload(response);
      this.predictionQuoteRequests = this.resolvePredictionRequestRows(response);
      this.predictionQuoteFeeds = predictionQuote?.feeds ?? [];
      const costDetails = this.resolvePredictionCostDetails(predictionQuote);
      this.predictionWeatherCost = costDetails.weatherCost;
      this.predictionFeedCost = costDetails.feedCost;
      this.predictionCost = costDetails.totalCost;
      this.predictionWeatherPoint = costDetails.weatherPoint;
      this.predictionFeedPoint = costDetails.feedPoint;
      this.predictionPointTotal = costDetails.totalPoint;
      this.predictionCostCurrency = costDetails.currency;
    } catch {
      if (requestSeq !== this.predictionCostRequestSeq) {
        return;
      }

      this.predictionCost = 0;
      this.predictionWeatherCost = 0;
      this.predictionFeedCost = 0;
      this.predictionWeatherPoint = 0;
      this.predictionFeedPoint = 0;
      this.predictionPointTotal = 0;
      this.predictionCostCurrency = 'USD';
      this.predictionCostError = 'Unable to retrieve the latest cost. Please try again.';
      this.predictionQuoteFeeds = [];
      this.predictionQuoteRequests = [];
    } finally {
      if (requestSeq === this.predictionCostRequestSeq) {
        this.predictionCostLoading = false;
        this.host.changDetectionRef.markForCheck();
      }
    }
  }

  retryPredictionCost(): void {
    void this.refreshPredictionCost();
  }

  private buildPredictionCostPayload(detections: SelectedDetection[]): any {
    const targets = this.getDetectionTargets(detections);
    const predictionWindow = this.getPredictionCostWindow();

    return {
      predictionInfo: this.getPredictionInfoIds(),
      target: targets.map((coordinates) => ({ coordinates })),
      openWindow: predictionWindow.openWindow,
      closeWindow: predictionWindow.closeWindow,
      taskId: this.host.collection?.taskId ?? this.host.currentTaskId ?? this.host.taskid ?? '',
      customerId: this.host.currentUser?.id,
    };
  }

  private getPredictionCostWindow(): { openWindow: string; closeWindow: string } {
    const openWindow = new Date();
    const closeWindow = new Date(openWindow.getTime() + 7 * 24 * 60 * 60 * 1000);

    return {
      openWindow: openWindow.toISOString(),
      closeWindow: closeWindow.toISOString(),
    };
  }

  private getPredictionInfoIds(): string[] {
    const sensors = Array.isArray(this.host.collection?.sensors)
      ? this.host.collection.sensors
      : [];
    const ids = new Set<string>();

    sensors.forEach((sensor: any) => {
      const services = [
        ...(sensor?.informationServices ?? []),
        ...(sensor?.InfoServices ?? []),
        ...(sensor?.AssociatedFeeds ?? []),
      ];

      services.forEach((service: any) => {
        (service?.predictionInfoDetails ?? []).forEach((detail: any) => {
          const id = detail?.id ?? detail?._id;
          if (id) ids.add(String(id));
        });
      });
    });

    return Array.from(ids);
  }

  getPredictionFeedName(): string {
    const sensors = Array.isArray(this.host.collection?.sensors)
      ? this.host.collection.sensors
      : [];

    for (const sensor of sensors) {
      const services = [
        ...(sensor?.informationServices ?? []),
        ...(sensor?.InfoServices ?? []),
        ...(sensor?.AssociatedFeeds ?? []),
      ];

      for (const service of services) {
        const detail = service?.predictionInfoDetails?.[0];
        const feedName = detail?.feedName ?? detail?.feed_name ?? service?.feedName ?? service?.name;
        if (feedName) {
          return String(feedName);
        }
      }
    }

    return '';
  }

  private getDetectionTargets(detections: SelectedDetection[]): number[][][] {
    return detections
      .flatMap((detection) => this.getDetectionRings(detection))
      .filter((ring) => ring.length >= 3);
  }

  private getDetectionRings(detection: SelectedDetection): number[][][] {
    const geometry = detection.geometry;
    const coordinates = geometry?.coordinates ?? detection.coordinates;

    return this.extractOuterCoordinateRings(coordinates);
  }

  private extractOuterCoordinateRings(coordinates: any): number[][][] {
    if (!Array.isArray(coordinates)) {
      return [];
    }

    if (this.isCoordinateRing(coordinates)) {
      return [this.normalizeCoordinateRing(coordinates)];
    }

    if (this.isCoordinateRing(coordinates?.[0])) {
      return [this.normalizeCoordinateRing(coordinates[0])];
    }

    return coordinates
      .flatMap((item: any) => this.extractOuterCoordinateRings(item))
      .filter((ring) => ring.length >= 3);
  }

  private isCoordinateRing(value: any): boolean {
    return (
      Array.isArray(value) &&
      Array.isArray(value?.[0]) &&
      Number.isFinite(Number(value[0][0])) &&
      Number.isFinite(Number(value[0][1]))
    );
  }

  private normalizeCoordinateRing(coordinates: any): number[][] {
    if (!Array.isArray(coordinates)) {
      return [];
    }

    const ring = coordinates
      .filter((point: any) => Array.isArray(point) && point.length >= 2)
      .map((point: any) => [Number(point[0]), Number(point[1])])
      .filter((point: number[]) => Number.isFinite(point[0]) && Number.isFinite(point[1]));

    if (ring.length > 2) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        return [...ring, [...first]];
      }
    }

    return ring;
  }

  private buildPredictionTargetFeatureCollection(detections: SelectedDetection[]): string {
    const features = detections
      .flatMap((detection, detectionIndex) =>
        this.getDetectionRings(detection)
          .filter((ring) => ring.length >= 3)
          .map((ring) => ({
            type: 'Feature',
            properties: this.predictionMode === 'all' ? {} : {
              class: detectionIndex + 1,
            },
            geometry: {
              type: 'Polygon',
              coordinates: [ring],
            },
          }))
      );

    return JSON.stringify({
      type: 'FeatureCollection',
      features,
    });
  }

  private buildPredictionCartData(sensorId: any, totalCost: number): any[] {
    const sensors = Array.isArray(this.host.collection?.sensors)
      ? this.host.collection.sensors
      : [];
    const selectedSensorId = sensorId ? String(sensorId) : '';
    const predictionRequest = this.predictionQuoteRequests.find((item: any) => {
      const itemId = item?.uuid ?? item?.id ?? item?.sensor_uuid ?? item?.sensorId;
      return selectedSensorId && itemId && String(itemId) === selectedSensorId;
    }) ?? this.predictionQuoteRequests[0];
    const sensor = predictionRequest ?? sensors.find((item: any) => {
      const itemId = item?.uuid ?? item?.id ?? item?.sensor_uuid ?? item?.sensorId;
      return selectedSensorId && itemId && String(itemId) === selectedSensorId;
    }) ?? sensors[0];

    if (!sensor) {
      return [];
    }

    return [{
      uuid: sensor?.uuid ?? sensor?.id ?? sensor?.sensor_uuid ?? sensorId,
      name: sensor?.name ?? sensor?.operator ?? sensor?.sensor,
      sensor: sensor?.sensor ?? sensor?.sensorType ?? sensor?.sensortype,
      resolution: sensor?.resolution ?? sensor?.resolution_tooltip ?? sensor?.resolutionTooltip,
      obfuscateName: sensor?.obfuscateName,
      collectionMode: this.normalizePredictionCollectionMode(sensor?.collectionMode),
      operatorKey: sensor?.operatorKey,
      cost: totalCost,
      point: this.isPointCustomer ? this.predictionPointTotal : (sensor?.point ?? 0),
      feed_point: this.isPointCustomer ? this.predictionFeedPoint : (sensor?.feed_point ?? 0),
      weather_ex_point: this.isPointCustomer ? this.predictionWeatherPoint : (sensor?.weather_ex_point ?? 0),
      total_point: this.isPointCustomer ? this.predictionPointTotal : (sensor?.total_point ?? 0),
      gst_data: 0,
      total: totalCost,
      checked: false,
    }];
  }

  private normalizePredictionCollectionMode(collectionMode: any): any[] {
    if (!Array.isArray(collectionMode)) {
      return [];
    }

    return collectionMode.map((mode: any) => ({
      key: mode?.key,
      value: mode?.value,
    }));
  }

  private buildPredictionMlTasks(sensorId: any): any[] {
    const feeds = Array.isArray(this.predictionQuoteFeeds)
      ? this.predictionQuoteFeeds
      : [];

    return feeds.map((feed: any) => ({
      ...feed,
      sensorId,
    }));
  }

  private getPredictionCheckoutWindow(): { openWindow: string; closeWindow: string } {
    const openWindow = new Date();
    const closeWindow = new Date(openWindow.getTime() + 7 * 24 * 60 * 60 * 1000);

    return {
      openWindow: openWindow.toISOString(),
      closeWindow: closeWindow.toISOString(),
    };
  }

  private resolvePredictionCostDetails(response: any): {
    weatherCost: number;
    feedCost: number;
    totalCost: number;
    weatherPoint: number;
    feedPoint: number;
    totalPoint: number;
    currency: string;
  } {
    const payload = this.resolvePredictionQuotePayload(response);
    const weatherCost = this.toFiniteCost(
      payload?.weather_ex_cost ??
      payload?.weatherCost ??
      payload?.weather_data_cost ??
      0
    );
    const feedCost = this.toFiniteCost(
      payload?.feed_cost ??
      payload?.feedCost ??
      payload?.predictionCost ??
      payload?.prediction_cost ??
      0
    );
    const fallbackTotal = this.toFiniteCost(
      payload?.total_cost ??
      payload?.totalCost ??
      payload?.cost ??
      payload?.price ??
      payload?.total ??
      payload?.taskcost ??
      0
    );
    const totalCost = fallbackTotal > 0 ? fallbackTotal : weatherCost + feedCost;
    const weatherPoint = this.toFiniteCost(
      payload?.weather_ex_point ??
      payload?.weatherExPoint ??
      payload?.weather_data_point ??
      payload?.weather_ex_points ??
      0
    );
    const feedPoint = this.toFiniteCost(
      payload?.feed_point ??
      payload?.feedPoint ??
      payload?.predictionPoint ??
      payload?.prediction_point ??
      payload?.feed_points ??
      0
    );
    const fallbackTotalPoint = this.toFiniteCost(
      payload?.total_point ??
      payload?.totalPoint ??
      payload?.point ??
      payload?.points ??
      payload?.total_points ??
      0
    );
    const totalPoint = fallbackTotalPoint > 0 ? fallbackTotalPoint : weatherPoint + feedPoint;
    const currency = payload?.currency ?? payload?.costCurrency ?? 'USD';

    return {
      weatherCost,
      feedCost,
      totalCost,
      weatherPoint,
      feedPoint,
      totalPoint,
      currency: String(currency || 'USD').toUpperCase(),
    };
  }

  private resolvePredictionQuotePayload(response: any): any {
    const payload = response?.results ?? response?.result ?? response?.responseResult ?? response?.response ?? response;
    const quoteSources = Array.isArray(payload) ? payload : [payload];

    for (const quoteSource of quoteSources) {
      if (quoteSource?.predictionQuote) {
        return quoteSource.predictionQuote;
      }

      const requestRows = Array.isArray(quoteSource?.request) ? quoteSource.request : [];
      const requestQuote = requestRows.find((request: any) => request?.predictionQuote)?.predictionQuote;
      if (requestQuote) {
        return requestQuote;
      }
    }

    return quoteSources[0];
  }

  private resolvePredictionRequestRows(response: any): any[] {
    const payload = response?.results ?? response?.result ?? response?.responseResult ?? response?.response ?? response;
    const quoteSources = Array.isArray(payload) ? payload : [payload];

    return quoteSources.flatMap((quoteSource: any) =>
      Array.isArray(quoteSource?.request) ? quoteSource.request : []
    );
  }

  private toFiniteCost(value: any): number {
    const cost = Number(value);
    return Number.isFinite(cost) ? cost : 0;
  }

  get isPointCustomer(): boolean {
    return this.host.currentUser?.pointsEligible === true;
  }

  get predictionChargeAmount(): number {
    return this.isPointCustomer ? this.predictionPointTotal : this.predictionCost;
  }

  private buildCheckoutCartBody(detections: SelectedDetection[]): any {
    const collection = this.host.collection ?? {};
    const taskId = this.host.collection?.taskId ?? this.host.currentTaskId ?? this.host.taskid ?? '';
    const sensorId = this.host.sensorSelected?.id ?? this.host.sensorSelected ?? this.host.collection?.sensors?.[0]?.id ?? '';
    const target = this.buildPredictionTargetFeatureCollection(detections);
    const checkoutWindow = this.getPredictionCheckoutWindow();

    const feedOnlyCartCost = this.isPointCustomer
      ? this.toFiniteCost(this.predictionFeedPoint) + this.toFiniteCost(this.predictionWeatherPoint)
      : this.toFiniteCost(this.predictionFeedCost) + this.toFiniteCost(this.predictionWeatherCost);
    const cartdata = this.buildPredictionCartData(sensorId, feedOnlyCartCost);
    const mlTaskSensorId = cartdata[0]?.uuid ?? sensorId;
    const mlTasks = this.buildPredictionMlTasks(mlTaskSensorId);

    return {
      customerid: this.host.currentUser?.id,
      taskid: taskId,
      project: collection?.projectname ?? collection?.project ?? 'Prediction Drift',
      taskname: collection?.taskname ?? 'Prediction Drift',
      openWindow: checkoutWindow.openWindow,
      closeWindow: checkoutWindow.closeWindow,
      target,
      cartdata,
      checked: true,
      taskFrequency: collection?.taskFrequency ?? collection?.frequency,
      mlTasks,
      feedOnlyPayment: true,
      taskStrategy: collection?.taskStrategy ?? 'User Initiated',
      purpose: collection?.purpose ?? collection?.purposeOption ?? 'Agriculture',
      intent: collection?.intent?.intent_key ?? collection?.intent ?? 'internalUse',
      repeatFrequency: collection?.repeatFrequency ?? '',
      noOfRepeats: collection?.noOfRepeats ?? 1,
      subDailyCount: collection?.subDailyCount,
      projectid: collection?.projectid ?? collection?.projectId ?? collection?.id,
      openWater: collection?.openWater ?? true,
      tipAndCueBasedOn: collection?.tipAndCueBasedOn ?? 'Rules',
      totalVaoCost: collection?.totalVaoCost ?? 0,
      totalSRPCost: collection?.totalSRPCost ?? 0,
    };
  }

  private async submitDriftCheckout(): Promise<void> {
    if (this.paymentRedirecting || this.predictionCostLoading) return;

    const detections = this.getCheckoutDetections();
    if (!detections.length) {
      this.host.snackBar.open('Select at least one detection before proceeding to payment.', 'Close', {
        duration: 4000,
        panelClass: ['background-Red'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return;
    }

    if (this.predictionCostError || this.predictionChargeAmount <= 0) {
      this.host.snackBar.open('Please wait for the latest prediction cost before proceeding.', 'Close', {
        duration: 4000,
        panelClass: ['background-Red'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return;
    }

    const userId = this.host.currentUser?.id;
    if (!userId) {
      this.host.snackBar.open('Unable to start payment because the user session is missing.', 'Close', {
        duration: 4000,
        panelClass: ['background-Red'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return;
    }

    this.paymentRedirecting = true;
    this.host.changDetectionRef.markForCheck();

    const cartBody = this.buildCheckoutCartBody(detections);
    try {
      await firstValueFrom(this.host.apiService.addToCart(userId, cartBody));
    } catch {
      this.host.snackBar.open('Unable to create the Prediction Drift payment item. Please try again.', 'Close', {
        duration: 4000,
        panelClass: ['background-Red'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      this.paymentRedirecting = false;
      this.host.changDetectionRef.markForCheck();
      return;
    }

    const sensorId = this.host.sensorSelected?.id ?? this.host.sensorSelected ?? this.host.collection?.sensors?.[0]?.id ?? '';
    try {
      this.host.localStorageService?.setSelectedSensors(
        detections.map(() => ({
          sensorId,
          taskId: cartBody.taskid,
          mode: this.predictionMode,
          cost: this.predictionChargeAmount,
          point: this.isPointCustomer ? this.predictionPointTotal : 0,
        }))
      );
    } catch (error) {
      console.warn('Unable to persist Prediction Drift selected sensors before payment redirect.', error);
    }

    try {
      const navigated = await this.host.router.navigate(['/payment'], {
        queryParams: { taskId: cartBody.taskid, type: 'drift-feed' },
      });

      if (!navigated) {
        this.host.snackBar.open('Prediction Drift was added to cart, but payment navigation was blocked.', 'Close', {
          duration: 4000,
          panelClass: ['background-Red'],
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
    } catch {
      this.host.snackBar.open('Prediction Drift was added to cart, but payment navigation failed.', 'Close', {
        duration: 4000,
        panelClass: ['background-Red'],
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    } finally {
      this.paymentRedirecting = false;
      this.host.changDetectionRef.markForCheck();
    }
  }

  // ── TODO: Future API methods ──────────────────────────────────────────────

  /**
   * TODO: POST to /api/prediction-drift/calculate-cost
   * Returns the cost for the given polygons.
   */
  async calculatePredictionCost(_polygons: SelectedDetection[]): Promise<number> {
    return this.predictionChargeAmount;
  }

  /**
   * TODO: POST to /api/prediction-drift/submit
   * Submits selected polygons for drift prediction.
   */
  async submitPrediction(_polygons: SelectedDetection[]): Promise<string> {
    return 'simulated-job-id';
  }

  /**
   * TODO: GET /api/prediction-drift/result/{jobId}
   * Fetches prediction result for a given job.
   */
  async fetchPredictionResult(_jobId: string): Promise<any> {
    return null;
  }
}
